import * as React from 'react';
import { CSSProperties, ReactNode } from 'react';
import { defined, formatMessageBundle, isEqual } from '@unifocus/js-utils';
import messages from '../../../localized-messages';
import { Icon } from '@unifocus/react-icons';
import { faChevronLeft, faChevronRight } from '@fortawesome/pro-light-svg-icons';
import { Button } from '../button';
import { HorizontalPanel } from '../../layout/horizontalpanel';
import { Select, Option } from '../select';
import { Tooltip } from '../../display/tooltip';

/**
 * {@docCategory NavigableDropdown}
 */
export interface NavigableDropdownProps<T> {

   /**
    * Provide the data for the component
    */
   data?: T[];

   /**
    * Set the value that represents what the 'current' button will select
    */
   currentValue?: T;

   /**
    * Set the currently selected value, if different from the 'current' value
    */
   selectedValue?: T;

   /**
    * Set whether or not to hide the 'current' button
    */
   hideCurrentButton?: boolean;

   /**
    * Set the text for the 'current' button
    */
   currentButtonText?: string;

   /**
    * Set the size of the component
    *
    * @defaultvalue middle
    */
   size?: 'large' | 'small' | 'middle';

   /**
    * Given a data item, return the value
    */
   valueProducer: (object: T) => string | number;

   /**
    * Given a data item, return the label to display
    */
   labelProducer: (item: T) => string | ReactNode;

   /**
    * Listen for value change events
    */
   onChange: (value: T) => void;

   /**
    * Set the tooltip for the 'current' button
    */
   currentButtonTooltip?: string;

   /**
    * Set the tooltip for the 'previous' button
    */
   previousButtonTooltip?: string;

   /**
    * Set the tooltip for the 'next' button
    */
   nextButtonTooltip?: string;

   /**
    * To set direction in which data will be navigated.
    *
    * @defaultvalue 'reversed'
    */
   arrowTraversalDirection?: 'reversed' | 'normal'

   /**
    * Whether or not to disable the component
    */
   disabled?: boolean;

   /**
    * Sets the width of the select component
    *
    * @defaultvalue 210
    */
   width?: string | number;

   /**
    * Sets styles on the grid item.
    */
   style?: CSSProperties;
}

export interface NavigableDropdownState<T> {
   selectedValue: T;
}

enum IncrementDecrement {
   INCREMENT,
   DECREMENT,
   CURRENT
}

const defaultData: any = [{
   value: '????????',
   label: '????????',
   isCurrentPeriod: false
}];

const dataItemToOption = <T extends any>(valueProducer: (object: T) => string | number, labelProducer: (object: T) => string | ReactNode) => {
   return (item: T) => {
      const value = valueProducer(item);
      return <Option key={value} value={value}>{labelProducer(item)}</Option>;
   };
};

const mapDataToSelectOptions = <T extends any>(data: T[], valueProducer: (object: T) => string | number, labelProducer: (object: T) => string | ReactNode) => {
   return data.map(dataItemToOption(valueProducer, labelProducer));
};

const determineIfButtonsAreDisabled = <T extends any>(data: T[],
                                                      selectedValue: T,
                                                      disableAllButtons: boolean,
                                                      isNormalTraversalDirection: boolean,
                                                      valueProducer: (object: T) => string | number) => {
   if(disableAllButtons){
      return {
         incrementDisabled: true,
         decrementDisabled: true
      };
   }

   const selectedValueValue = valueProducer(selectedValue);
   const currentIndex: number =  data.findIndex((item:T) => valueProducer(item) === selectedValueValue);
   const atDataEnd: boolean = currentIndex >= data.length - 1;
   const atDataStart: boolean = currentIndex <= 0;

   return {
      incrementDisabled: isNormalTraversalDirection ? atDataStart : atDataEnd,
      decrementDisabled: isNormalTraversalDirection ? atDataEnd : atDataStart
   };
};

const determineCurrentValueExists = (data: any, currentValue: any) => {
   if (currentValue) {
      return currentValue;
   }
   else {
      if (data) {
         const filteredData = data.filter((x: any) => x.isCurrentPeriod === true);
         return (filteredData.length === 0) ? filteredData : filteredData[0];
      }
   }
};

const determineIfAllButtonsAreDisabled = (data:any) => {
   return !data;
};

const determineSizeExists = (size:any) => {
   return (!size) ? 'middle': size;
};

const determineIfCurrentButtonDisabled = (currentValue:any, hideCurrentButton:any) => {
   return (currentValue.length === 0) ? true : hideCurrentButton;
};

const convertSize = (width?: string | number) => {
   if (!defined(width)) {
      return "210px";
   }

   if (typeof width === 'string' || width === 0) {
      return width;
   }

   return width + 'px';
};

export class NavigableDropdown<T = any> extends React.Component<NavigableDropdownProps<T>, NavigableDropdownState<T>> {
   private data: any;
   private disableAllButtons: boolean;
   private defaultCurrentValue: T;
   private size: 'large' | 'small' | 'middle';
   private hideCurrentButton: boolean;

   private readonly defaultCurrentButtonText: string = formatMessageBundle(messages, 'res_current');
   private readonly defaultCurrentButtonTooltip: string = formatMessageBundle(messages, 'res_selectTheCurrentOption');
   private readonly defaultRightButtonTooltip: string = formatMessageBundle(messages, 'res_next');
   private readonly defaultLeftButtonTooltip: string = formatMessageBundle(messages, 'res_previous');

   constructor(props: NavigableDropdownProps<T>) {
      super(props);

      this.setInitialValues();

      this.state = {
         selectedValue: props.selectedValue || this.defaultCurrentValue,
      };
   }

   componentDidUpdate(prevProps: Readonly<NavigableDropdownProps<T>>, prevState: Readonly<NavigableDropdownState<T>>, snapshot?: any): void {
      if (!isEqual(prevProps.data, this.props.data)) {
         this.setInitialValues();
         this.setState({ selectedValue: this.props.selectedValue || this.defaultCurrentValue });
      }
   }

   render() {
      const { incrementDisabled, decrementDisabled } = determineIfButtonsAreDisabled(this.data,
                                                                                     this.state.selectedValue,
                                                                                     this.disableAllButtons,
                                                                                     this.props.arrowTraversalDirection === 'normal',
                                                                                     this.props.valueProducer);

      const width = convertSize(this.props.width);
      const style: CSSProperties = {
         width: width,
         minWidth: width,
         ...this.props.style
      };

      return (
         <HorizontalPanel className="unifocus-navigabledropdown" nowrap={true}>
            <Select
               size={this.size}
               className="unifocus-selectdropdown"
               onChange={this.onChange}
               disabled={this.disableAllButtons}
               value={this.props.valueProducer(this.state.selectedValue)}
               style={style}
            >
               {mapDataToSelectOptions(this.props.data || defaultData, this.props.valueProducer, this.props.labelProducer)}
            </Select>
            {
               !this.hideCurrentButton &&
               <Tooltip title={this.props.currentButtonTooltip || this.defaultCurrentButtonTooltip}>
                  <Button
                     size={this.size}
                     className="unifocus-current-btn"
                     onClick={this.jumpToCurrent}
                     disabled={this.disableAllButtons}
                  >
                     {this.props.currentButtonText || this.defaultCurrentButtonText}
                  </Button>
               </Tooltip>
            }
            <HorizontalPanel nowrap={true}>
               <Tooltip title={this.props.previousButtonTooltip || this.defaultLeftButtonTooltip}>
                  <Button
                     className="unifocus-left-btn"
                     onClick={this.incrementElement}
                     disabled={incrementDisabled}
                  >
                     <Icon icon={faChevronLeft}/>
                  </Button>
               </Tooltip>
               <Tooltip title={this.props.nextButtonTooltip || this.defaultRightButtonTooltip}>
                  <Button
                     className="unifocus-right-btn"
                     onClick={this.decrementElement}
                     disabled={decrementDisabled}
                  >
                     <Icon icon={faChevronRight}/>
                  </Button>
               </Tooltip>
            </HorizontalPanel>
         </HorizontalPanel>
      );
   }

   private setInitialValues = () => {
      this.data = this.props.data || defaultData;
      this.disableAllButtons = this.props.disabled || determineIfAllButtonsAreDisabled(this.props.data);
      this.defaultCurrentValue = determineCurrentValueExists(this.props.data || defaultData, this.props.currentValue);
      this.size = determineSizeExists(this.props.size);
      this.hideCurrentButton = determineIfCurrentButtonDisabled(this.defaultCurrentValue, this.props.hideCurrentButton);
   };

   /**
    * @name        jumpToCurrent
    * @description Moves selected value to the designated current period
    */
   private jumpToCurrent = () => {
      this.updateStateFromButton(IncrementDecrement.CURRENT);
   }
   /**
    * @name        decrementElement
    * @description When right button is pressed, navigate down the array of data provided
    */
   private decrementElement = () => {
      this.updateStateFromButton(this.props.arrowTraversalDirection === 'normal' ? IncrementDecrement.INCREMENT
                                                                                 : IncrementDecrement.DECREMENT);
   }
   /**
    * @name        incrementElement
    * @description When left button is pressed, navigate up the array of data provided
    */
   private incrementElement = () => {
      this.updateStateFromButton(this.props.arrowTraversalDirection === 'normal' ? IncrementDecrement.DECREMENT
                                                                                 : IncrementDecrement.INCREMENT);
   }
   /**
    * @name updateStateFromButton
    * @param type - Which button type
    * @description When a button is pressed, update the current value and send data to parent component
    */
   private updateStateFromButton = (type: IncrementDecrement) => {
      const newValue = this.getNewValue(type);
      this.updateCurrentValueAndFireOnChange(newValue);
   }
   /**
    * @name getNewValue
    * @param type - Which button type
    * @description returns index of selected value
    */
   private getNewValue = (type: IncrementDecrement) => {

      let newIndex;
      if (type === IncrementDecrement.CURRENT && this.props.currentValue) {
         const currentValueValue = this.props.valueProducer(this.props.currentValue);
         newIndex =  this.data.findIndex((item:T) => this.props.valueProducer(item) === currentValueValue);
      }
      else {
         const selectedValueValue = this.props.valueProducer(this.state.selectedValue);
         newIndex = this.data.findIndex((item:T) => this.props.valueProducer(item) === selectedValueValue);
         if (type !== IncrementDecrement.CURRENT) {
            newIndex = type === (IncrementDecrement.INCREMENT) ? newIndex + 1 : newIndex - 1;
         }
      }

      return this.data[newIndex];
   }
   /**
    * @name onChange
    * @param selectValue - Select value
    */
    private onChange = (selectValue: any) => {
       const matchingDataItem = this.data.find((item: T) => this.props.valueProducer(item) == selectValue);
       matchingDataItem && this.updateCurrentValueAndFireOnChange(matchingDataItem);
   }
   /**
    * @name updateCurrentValueAndFireOnChange
    * @param newValue - the new value
    * @description updates currentValue state and sends the selected data object to the parent component
    */
   private updateCurrentValueAndFireOnChange = (newValue: T) => {
      this.setState({ selectedValue: newValue });
      this.props.onChange(newValue);
   }
}
